<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";

  .nav-item {
    width: 100%;
    height: utils.in_rem(36px);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: utils.in_rem(6px);

    &:focus {
      outline: none;
    }

    &:active,
    &:hover,
    &:focus-within,
    &.highlight {
      .wrapper {
        @include colors.background-color(interfaceTransparentWhite_15);
      }
    }

    &:hover .wrapper {
      cursor: pointer;
    }

    .wrapper {
      height: utils.in_rem(32px);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 utils.in_rem(16px);
      border-radius: utils.in_rem(36px);
      position: relative;

      &.expanded {
        width: 100%;
        justify-content: space-between;
        padding: 0 utils.in_rem(8px) 0 utils.in_rem(16px);
      }

      .icon-with-name {
        display: flex;
        align-items: center;
        gap: utils.in_rem(8px);

        .icon-container {
          width: utils.in_rem(16px);
          height: utils.in_rem(16px);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .name {
          @include colors.color(interfaceTransparentWhite_90);
          @include mixins.sidebar();
          white-space: nowrap;
        }
      }

      .badge-wrapper {
        position: absolute;
        top: utils.in_rem(-3px);
        right: utils.in_rem(9px);
      }

      .new {
        @include colors.background-color(accentStatusSeaGreen);
        width: utils.in_rem(8px);
        height: utils.in_rem(8px);
        position: absolute;
        top: utils.in_rem(-2px);
        right: utils.in_rem(10px);
        border-radius: 50%;
      }

      .trial {
        @include colors.background-color(accentTrialPink);
        width: utils.in_rem(8px);
        height: utils.in_rem(8px);
        position: absolute;
        top: utils.in_rem(-2px);
        right: utils.in_rem(10px);
        border-radius: 50%;
      }

      .dropdown-icon-container {
        width: utils.in_rem(24px);
        height: utils.in_rem(24px);
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }
</style>

<script>
  import Badge from "@sourcewhale/shared/svelte-design-system/components/atoms/Badge.svelte";
  import Icon from "@sourcewhale/shared/svelte-design-system/components/atoms/Icon.svelte";
  import { currentPath } from "./stores/current-path-store";
  import { sidebarIsExpanded } from "./stores/sidebar-store";

  export let navItem;
  export let onclick = () => {}; // callback for dropdown open/close

  $: ({ badgeValue, badgeClass } = navItem);

  const hasDropdownMenu = navItem.menu;
  const dropdownLinks = hasDropdownMenu ? navItem.menu.map((x) => x.link) : [];
  let targetElement;
  const links = [navItem.link, ...dropdownLinks];
  const highlight =
    links.includes($currentPath) ||
    ["/campaigns", "/projects"].some(
      (x) => links.includes(x) && $currentPath === x.slice(0, -1)
    );

  let isDropdownOpen = false;

  $: if (!$sidebarIsExpanded) {
    isDropdownOpen = false;
    targetElement?.blur();
  }

  const clickHandler = () => {
    if (hasDropdownMenu) {
      isDropdownOpen = !isDropdownOpen;
      if (!isDropdownOpen && targetElement) targetElement.blur();
    }
    onclick();
  };

  $: badgeIcon = `${(highlight || isDropdownOpen ? navItem.activeIcon : navItem.icon) || ""}`;

  $: dropdownIcon = isDropdownOpen
    ? "far fa-chevron-up"
    : "far fa-chevron-down";
</script>

<a
  bind:this={targetElement}
  href={navItem.link}
  class="nav-item"
  class:highlight
  data-testid="navItem_{navItem.id}"
  tabindex="0"
  on:click|stopPropagation={clickHandler}
>
  <div class="wrapper" class:expanded={$sidebarIsExpanded}>
    <div class="icon-with-name">
      {#if badgeIcon}
        <div class="icon-container">
          <Icon class="{badgeIcon} color-interfaceTransparentWhite_90" />
        </div>
      {/if}
      {#if $sidebarIsExpanded}
        <span class="name">{navItem.name}</span>
      {/if}
    </div>
    {#if !$sidebarIsExpanded}
      {#if badgeValue}
        {#if badgeClass === "new"}
          <span class="new"></span>
        {:else if badgeClass === "trial"}
          <span class="trial"></span>
        {:else}
          <div class="badge-wrapper">
            <Badge position="right" contrast={badgeClass} value={badgeValue} />
          </div>
        {/if}
      {/if}
    {:else}
      {#if badgeValue}
        <Badge
          position="center"
          inline
          contrast={badgeClass}
          value={badgeValue}
        />
      {/if}
      {#if hasDropdownMenu}
        <div class="dropdown-icon-container">
          <Icon class="{dropdownIcon} color-interfaceWhite" size="xs" />
        </div>
      {/if}
    {/if}
  </div>
</a>
